import { useMemo, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import type { PostPlatform, PublishingAccount } from '../../types/publishing';

// Publishing composer — searchable account picker (kyck accounts with FB/IG/GBP
// connected). Lightweight combobox: filter input + a scrollable result list.

const PLATFORM_ICON: Record<PostPlatform, string> = {
  facebook: 'FB',
  instagram: 'IG',
  googlebusiness: 'GBP',
};

export function AccountCombobox({
  accounts,
  value,
  onChange,
  disabled,
}: {
  accounts: PublishingAccount[];
  value: string;
  onChange: (accountId: string) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = accounts.find((a) => a.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return accounts.slice(0, 50);
    return accounts
      .filter((a) => a.name.toLowerCase().includes(q) || (a.brandName ?? '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [accounts, query]);

  if (selected && !open) {
    return (
      <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">{selected.name}</p>
          <p className="text-xs text-slate-500">
            {selected.brandName ? `${selected.brandName} · ` : ''}
            {connectedChips(selected)}
          </p>
        </div>
        {!disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>
            Change
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Input
        autoFocus
        value={query}
        disabled={disabled}
        placeholder="Search accounts…"
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
        {filtered.length === 0 && (
          <p className="px-3 py-2 text-sm text-slate-400">No postable accounts match.</p>
        )}
        {filtered.map((a) => (
          <button
            key={a.id}
            type="button"
            className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
            onClick={() => {
              onChange(a.id);
              setOpen(false);
              setQuery('');
            }}
          >
            <span className="min-w-0">
              <span className="block text-sm text-slate-900 truncate">{a.name}</span>
              {a.brandName && <span className="block text-xs text-slate-400">{a.brandName}</span>}
            </span>
            <span className="text-[10px] font-medium text-slate-400">{connectedChips(a)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function connectedChips(a: PublishingAccount): string {
  return (Object.keys(a.connected) as PostPlatform[])
    .filter((p) => a.connected[p])
    .map((p) => PLATFORM_ICON[p])
    .join(' · ');
}
